import React from 'react';
import { Box, Container, Typography, Button, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../analytics';
import LatestFigures from '../components/salaryCalculator/HomePage/LatestFigures';
import { WebApplicationSchema, HowToSchema } from '../components/StructuredData';
import LeadForm from '../components/LeadForm';
import TestimonialLeadForm from '../components/TestimonialLeadForm';
import { isFeatureEnabled } from '../featureFlags';
import { makeMeta, makeWebsiteJsonLd, makeOrganizationJsonLd } from '../utils/seo';

const Homepage = () => {
  const navigate = useNavigate();
  
  // SEO metadata with structured data
  const websiteJsonLd = makeWebsiteJsonLd({
    siteName: 'SalaryTools', 
    url: 'https://salarytools.co.uk'
  });
  
  const organizationJsonLd = makeOrganizationJsonLd({
    name: 'SalaryTools',
    url: 'https://salarytools.co.uk',
    logoUrl: 'https://salarytools.co.uk/logo.png'
  });

  return (
    <Box>
      {makeMeta({
        title: 'UK Take Home Pay Calculator',
        description: "The UK's only salary calculator with visual tax bands! See exactly where your tax goes and adjust salary in real-time. Interactive visual breakdown of tax, NI and take-home pay.",
        canonical: 'https://salarytools.co.uk/',
        jsonLd: [websiteJsonLd, organizationJsonLd]
      })}
      <WebApplicationSchema />
      <HowToSchema 
        name="How to Calculate Your UK Take Home Pay"
        description="Learn how to calculate your take-home pay using our UK salary calculator"
        steps={[
          {
            name: "Enter your annual salary",
            text: "Input your gross annual salary into the calculator field"
          },
          {
            name: "Add additional details",
            text: "Include pension contributions, student loan repayments, and overtime if applicable"
          },
          {
            name: "View your breakdown",
            text: "See your take-home pay, tax, and National Insurance contributions displayed visually"
          }
        ]}
      />

      {/* HMRC Figures Badge */}
      <LatestFigures />

      <Box
        sx={{
          bgcolor: 'primary.50',
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: { xs: 4, md: 6 }
        }}
      >
        <Container sx={{ textAlign: 'center', paddingY: 6 }}>
          {/* Main Heading */}
          <Typography variant="h1" sx={{ 
            fontWeight: 'bold', 
            marginBottom: 4,
            color: 'text.primary',
            fontSize: { xs: '2.25rem', md: '3.5rem' },
            lineHeight: { xs: 1.2, md: 1.1 }
          }}>
            The UK's Only Visual Tax Band Calculator
          </Typography>
          
          {/* Unique Selling Point Badge */}
          <Box sx={{ 
            backgroundColor: 'background.paper', 
            padding: { xs: 2, md: 3 }, 
            borderRadius: 'xl', 
            marginBottom: 4,
            display: 'inline-block',
            border: 1,
            borderColor: 'divider',
            boxShadow: 2
          }}>
            <Typography variant="body2" sx={{ 
              color: 'primary.main', 
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}>
              🎯 UNIQUE: See your tax bands visually + adjust salary instantly
            </Typography>
          </Box>
          
          {/* Subheading */}
          <Typography variant="h4" sx={{ 
            marginBottom: 5,
            color: 'text.secondary',
            lineHeight: 1.5,
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            maxWidth: '800px',
            margin: '0 auto',
          }}>
            The only UK calculator that shows your tax breakdown in <strong>interactive visual bands</strong><br />
            <strong>Instantly adjust your salary and watch your tax change in real-time.</strong>
          </Typography>
          
          {/* Social Proof Stats */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: { xs: 3, md: 6 }, 
            marginBottom: 6,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                HMRC
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Official Rates
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                100%
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Free Forever
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                2025
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Tax Year Ready
              </Typography>
            </Box>
          </Box>

          {/* Call-to-Action Buttons */}
          <Grid container justifyContent="center" spacing={{ xs: 2, md: 4 }} sx={{ marginBottom: 4 }}>
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                    padding: { xs: 2, md: 3 },
                    minWidth: { xs: '200px', md: '250px' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => {
                    trackEvent("CTA", "Click", "Homepage CTA - Learn About Your Salary");
                    navigate('/salary-calculator');
                  }}
                >
                  Learn About Your Salary <ArrowForwardIcon sx={{ ml: 1 }} />
                </Button>
              </Grid>
              {isFeatureEnabled('SALARY_COMPARISON') && (
                <Grid>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{
                      fontSize: { xs: '1.1rem', md: '1.2rem' },
                      padding: { xs: 2, md: 2.5 },
                      minWidth: { xs: '180px', md: '220px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => {
                      trackEvent("CTA", "Click", "Homepage CTA - Compare Salaries");
                      navigate('/salary-comparison');
                    }}
                  >
                    Compare Two Salaries
                  </Button>
                </Grid>
              )}
          </Grid>
        </Container>

        {/* Key Features Section */}
        <Box sx={{ textAlign: 'left', paddingTop: 8, paddingBottom: 4, maxWidth: '800px', margin: '0 auto' }}>
          <Typography variant="h5" sx={{ textAlign: 'center', marginBottom: 4, fontWeight: 'bold', color: 'text.primary' }}>
            Why Choose Our Calculator Over Others?
          </Typography>
          <List sx={{ padding: 0 }}>
            <ListItem sx={{ paddingY: 2, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 40, paddingTop: 0.5 }}>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Visual Tax Bands (UK's ONLY)" 
                secondary="See exactly how much tax you pay in each bracket with interactive visual bands - no other UK calculator does this!"
                primaryTypographyProps={{ fontWeight: 'bold', marginBottom: 1 }}
                secondaryTypographyProps={{ fontSize: '0.95rem', lineHeight: 1.5 }}
              />
            </ListItem>
            <ListItem sx={{ paddingY: 2, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 40, paddingTop: 0.5 }}>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Real-Time Salary Adjustment" 
                secondary="Move the salary slider and instantly watch your tax, NI, and take-home pay update in real-time. Perfect for salary negotiations!"
                primaryTypographyProps={{ fontWeight: 'bold', marginBottom: 1 }}
                secondaryTypographyProps={{ fontSize: '0.95rem', lineHeight: 1.5 }}
              />
            </ListItem>
            <ListItem sx={{ paddingY: 2, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 40, paddingTop: 0.5 }}>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Complete Accuracy for 2025" 
                secondary="Always up-to-date with the latest HMRC rates, including pension contributions, student loans, and Scottish tax rates."
                primaryTypographyProps={{ fontWeight: 'bold', marginBottom: 1 }}
                secondaryTypographyProps={{ fontSize: '0.95rem', lineHeight: 1.5 }}
              />
            </ListItem>
            <ListItem sx={{ paddingY: 2, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 40, paddingTop: 0.5 }}>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Works Everywhere" 
                secondary="Perfect on desktop, tablet, or mobile. No app downloads, no sign-ups, completely free forever."
                primaryTypographyProps={{ fontWeight: 'bold', marginBottom: 1 }}
                secondaryTypographyProps={{ fontSize: '0.95rem', lineHeight: 1.5 }}
              />
            </ListItem>
          </List>
          
            {/* Testimonial/Social Proof */}
            <Box sx={{ 
              backgroundColor: 'background.paper', 
              padding: { xs: 3, md: 4 }, 
              borderRadius: 'xl', 
              marginTop: 5,
              textAlign: 'center',
              border: 1,
              borderColor: 'divider',
              boxShadow: 2,
              maxWidth: '600px',
              margin: '5rem auto 0 auto'
            }}>
            <Typography variant="h6" sx={{ 
              fontStyle: 'italic', 
              marginBottom: 2, 
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.6,
              color: 'text.primary'
            }}>
              "Finally, a UK salary calculator that actually shows me WHERE my tax goes! The visual bands make it so much clearer than spreadsheets."
            </Typography>
              <Typography variant="body2" sx={{ 
                color: 'primary.600', 
                fontWeight: 'semiBold',
                fontSize: '0.9rem'
              }}>
                — Sarah M., Financial Planner
              </Typography>
            </Box>
        </Box>
      </Box>

      {/* Standard Lead Form Section */}
      {isFeatureEnabled('STANDARD_LEAD_FORM') && (
        <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                Stay Updated with UK Tax Changes
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Get notified when tax rates change and receive tips for maximizing your take-home pay
              </Typography>
            </Box>
            <LeadForm 
              title="Never Miss Tax Updates" 
              subtitle="Join thousands of UK professionals who stay informed about tax changes that affect their salary"
            />
          </Container>
        </Box>
      )}

      {/* Testimonial Lead Form Section - A/B Test Version */}
      {isFeatureEnabled('TESTIMONIAL_LEAD_FORM') && (
        <Box sx={{ py: 6 }}>
          <TestimonialLeadForm 
            testimonial="This is the best salary calculator I've found - the visual breakdown changed how I think about my finances!"
            title="Get exclusive access to our tips on maximizing your take-home pay"
            subtitle="Be the first to know about changes to UK tax rates and how they affect your salary"
            buttonText="Get instant access"
            consentText="By submitting, you agree to receive helpful salary tips from SalaryTools."
          />
        </Box>
      )}

    </Box>
  );
};

export default Homepage;
